/**
 * Utility functions for any storefront wizard region
 * TO-DO: refactor StorefrontWizardRegion and possibly CentralizedWizardRegion to make use of this utils file
 */

import * as React from "react";
import { FlexComponent } from "src/typings/flexFramework/FlexComponent";
import {
  BlossomComponentFactory,
  BlossomComponentFactoryProps,
} from "src/components/flexFramework/domain/BlossomComponentFactory";
import { ClientWizardStateStore } from "src/stores/wizard/ClientWizardStateStore";

/**
 *
 * @param flexModules Array of flex modules coming from the flex manager
 * Returns all modules corresponding to a wizard LOB form
 */
export const getWizardLOBFlexComponents = (flexModules: FlexComponent[]): FlexComponent[] => {
  return flexModules.filter((module: FlexComponent) => {
    return module && module.id && module.id.includes("wizard");
  });
};

/**
 *
 * @param children Array of react components output by BlossomComponent
 * Returns all react components corresponding to a wizard LOB form
 */
export const getWizardLOBFormChildren = (children: React.ReactElement[]): React.ReactElement[] => {
  return children.filter((child) => {
    const { key } = child;

    return key && key.toString().includes("wizard") && !key.toString().includes("biased");
  });
};

/**
 *
 * @param flexModules Array of flex modules coming from the flex manager
 * Returns all modules corresponding to a wizard LOB form
 */
export const getBiasedWizardLOBFlexComponent = (flexModules: FlexComponent[]): FlexComponent[] => {
  return flexModules.filter((module: FlexComponent) => {
    return module?.id?.includes("biased");
  });
};

/**
 *
 * @param children Array of react components output by BlossomComponent
 * Returns all react components that do not correspond to a wizard LOB form
 * Does not categorize react components further
 */
export const getNonWizardFormChildren = (children: React.ReactElement[]): React.ReactElement[] => {
  return children
    .filter((child) => {
      const { key } = child;

      return key && !key.toString().includes("wizard");
    })
    .map((child) => {
      const { key } = child;

      const childComponent = BlossomComponentFactory.get(child.props as BlossomComponentFactoryProps);
      const childComponentModel = childComponent?.model as any;
      if (childComponentModel && childComponentModel.horizontalCenter === false) {
        return (
          <div key={`parent-${key}`} className="prevent-justify-center">
            {child}
          </div>
        );
      }

      return <div key={`parent-${key}`}>{child}</div>;
    });
};

/**
 * Function part of test 33946; will be extracted into package after early signal
 */
export const emitUserInteractionDomainEvent = () => {
  const payload = {
    schemaName: "UserInteraction",
    schemaVersion: 1,
    payload: {
      event: {
        event_type: "Experience Usable",
        event_category: "Landing",
        event_name: "Home Usable",
        page_name: "Home",
      },
      timestamps: {
        sent_at: Date.now(),
        sent_at_timezone_offset_minutes: new Date().getTimezoneOffset(),
      },
    },
  };

  if (window?.location) {
    payload.payload["request"] = payload.payload["request"] || {};
    payload.payload["request"]["request_url"] = window.location.href;
  }

  if (document) {
    payload.payload["request"] = payload.payload["request"] || {};
    payload.payload["request"]["referrer_url"] = document.referrer;
  }

  const payloadString = JSON.stringify(payload);
  const httpRequest = new XMLHttpRequest();

  httpRequest.open("POST", "/uide/v1/collect", true);
  httpRequest.setRequestHeader("Content-type", "application/json");
  httpRequest.send(payloadString);
};

/**
 * Based on a list of ReactElement, will filter and return
 * the ones that are part of Packages as standalone LOBs
 * i.e. Hotels, Flights and Cars
 */
export const getStandaloneLOBModulesInPackages = (children: React.ReactElement[]) => {
  const standaloneLobsInPackages = ["wizard-hotel-pwa-v2", "wizard-flight-pwa", "wizard-car-pwa"];

  return children.filter((lobForm: React.ReactElement) => {
    const { name } = lobForm.props.flexComponent;

    return standaloneLobsInPackages.includes(name);
  });
};

/**
 * This function overrides the configs for the biased wizard
 * Which takes flexcomponent and wizardState as input.
 * Used in StoreFrontWizardRegionMeso
 */
export const getMesoWizardModuleData = (mesoWizardModule: FlexComponent[], wizardState: ClientWizardStateStore) => {
  const biasingModel = mesoWizardModule[0].model;

  const globalConfig = wizardState.globalWizardState.config;
  globalConfig.halfWizard = biasingModel.halfWizard;
  globalConfig.selectedProduct = biasingModel.selectedProduct;
  globalConfig.defaultOrigin = biasingModel.biasingOriginAttributeValue;

  globalConfig.defaultMultipleDestination =
    biasingModel.biasingDestinationAttributeMap !== undefined
      ? biasingModel.biasingDestinationAttributeMap[""]
      : undefined;
  globalConfig.defaultDestination = biasingModel.biasingDestinationAttributeValue;
  globalConfig.defaultMultipleOrigin =
    biasingModel.biasingOriginAttributeMap !== undefined ? biasingModel.biasingOriginAttributeMap[""] : undefined;
  globalConfig.destinationDropdownTypeahead = biasingModel.destDropdownTypeahead;
  globalConfig.calendarFilterCarrierCode = biasingModel.calendarFilterCarrierCode;

  const hotelConfig = wizardState.hotelWizardState.config;
  wizardState.hotelWizardState.overrideConfig(() => {
    hotelConfig.hotelID = biasingModel.biasingAttributeHotelValue;
    hotelConfig.hotelName = biasingModel.biasingAttributeHotelName;
    hotelConfig.hotelChainID = biasingModel.biasingAttributeHotelChainID;
    hotelConfig.multiHotelIDs =
      biasingModel.biasingAttributeMultiHotelValue && biasingModel.biasingAttributeMultiHotelValue.split(";");
    hotelConfig.isBiasedWizardEmpty = biasingModel.empty;
  });

  const carConfig = wizardState.carWizardState.config;
  wizardState.carWizardState.overrideConfig(() => {
    carConfig.carType = biasingModel.biasingAttributeCarType;
    carConfig.carVendorCode = biasingModel.biasingAttributeCarVendorCode;
    carConfig.carVendorName = biasingModel.biasingAttributeCarVendorName;
    carConfig.carDiscountTypeCode = biasingModel.carDiscountTypeCode;
    carConfig.carDiscountTypeName = biasingModel.carDiscountTypeName;
    carConfig.carDiscountCode = biasingModel.carDiscountCode;
    carConfig.showCarDiscount = biasingModel.showCarDiscount;
    carConfig.isBiasedWizardEmpty = biasingModel.empty;
  });

  const cruiseConfig = wizardState.cruiseWizardState.config;
  wizardState.cruiseWizardState.overrideConfig(() => {
    cruiseConfig.cruiseLine = biasingModel.biasingAttributeCruiseLine;
  });

  const flightConfig = wizardState.flightWizardState.config;
  wizardState.flightWizardState.overrideConfig(() => {
    flightConfig.airlineOptions = biasingModel.airlineOptions;
    flightConfig.cabinClass = biasingModel.biasedCabinClass;
  });

  const packageConfig = wizardState.packageWizardState.config;
  wizardState.packageWizardState.overrideConfig(() => {
    packageConfig.multiHotelIDs =
      biasingModel.biasingAttributeMultiHotelValue && biasingModel.biasingAttributeMultiHotelValue.split(";");
  });
};
